import {
  Input,
  InputGroup,
  Placement,
  type SelectOption,
} from '@aus-platform/design-system';
import { isEmpty, isNil } from 'lodash';
import { type DashboardDataCategoryEntry } from 'shared/api';
import { clampDecimalInput, preventNonNumber } from 'shared/helpers';
import { computeInSituVolume, computeTotalLeadDistance } from './helpers';
import {
  type LeadDistanceCardProps,
  type LeadDistanceNumberField,
} from './types';

export const LeadDistanceCard: React.FC<LeadDistanceCardProps> = ({
  entry,
  errors,
  category3Config,
  category4Config,
  selectStyles,
  sortOptionsByActive,
  formatOptionLabel,
  onSelectChange,
  onNumberChange,
  onFieldBlur,
  onFieldFocus,
}) => {
  // Variables.
  const inSituVolume = computeInSituVolume(
    entry.dumpVolume,
    entry.bulkingFactor,
  );

  const totalLeadDistance = computeTotalLeadDistance(entry);

  const isCategory3OptionsEmpty = isEmpty(category3Config?.options);
  const isCategory4OptionsEmpty = isEmpty(category4Config?.options);

  // Handlers.
  const handleNumberChange =
    (field: LeadDistanceNumberField) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onNumberChange(
        entry.clientId,
        field,
        clampDecimalInput(event.target.value, 2, false),
      );
    };

  const isOptionDisabled = (option: SelectOption<DashboardDataCategoryEntry>) =>
    !option.value.isActive;

  return (
    <div className="dashboard-modal__lead-distance__card">
      <div className="dashboard-modal__lead-distance__selection-block">
        <InputGroup>
          <Input.Label isRequired>{category3Config?.name}</Input.Label>

          <Input.Select
            placeholder="Select"
            options={sortOptionsByActive(category3Config?.options)}
            value={entry.category3}
            error={errors.category3}
            isDisabled={isCategory3OptionsEmpty}
            isOptionDisabled={isOptionDisabled}
            formatOptionLabel={formatOptionLabel}
            styles={selectStyles}
            onChange={(option: SelectOption<DashboardDataCategoryEntry>) =>
              onSelectChange(entry.clientId, 'category3', option)
            }
            onBlur={() => onFieldBlur(entry.clientId, 'category3')}
            onFocus={() => onFieldFocus(entry.clientId, 'category3')}
          />
        </InputGroup>

        <InputGroup>
          <Input.Label isRequired>{category4Config?.name}</Input.Label>

          <Input.Select
            placeholder="Select"
            options={sortOptionsByActive(category4Config?.options)}
            value={entry.category4}
            error={errors.category4}
            isDisabled={isCategory4OptionsEmpty}
            isOptionDisabled={isOptionDisabled}
            formatOptionLabel={formatOptionLabel}
            styles={selectStyles}
            onChange={(option: SelectOption<DashboardDataCategoryEntry>) =>
              onSelectChange(entry.clientId, 'category4', option)
            }
            onBlur={() => onFieldBlur(entry.clientId, 'category4')}
            onFocus={() => onFieldFocus(entry.clientId, 'category4')}
          />
        </InputGroup>
      </div>

      {/* Dump Volume & Bulking Factor */}
      <div className="dashboard-modal__lead-distance__selection-block">
        <InputGroup>
          <Input.Label
            isRequired
            info="The loose volume of material moved to the dump."
            infoPlacement={Placement.Top}
            tooltipClassname="dashboard-modal__lead-distance-tooltip"
          >
            Dump Volume (m³)
          </Input.Label>

          <Input.Number
            placeholder="Enter Value"
            value={entry.dumpVolume ?? ''}
            isInvalid={!!errors.dumpVolume}
            error={errors.dumpVolume}
            onKeyDown={preventNonNumber}
            onChange={handleNumberChange('dumpVolume')}
            onBlur={() => onFieldBlur(entry.clientId, 'dumpVolume')}
            onFocus={() => onFieldFocus(entry.clientId, 'dumpVolume')}
          />
        </InputGroup>

        <InputGroup>
          <Input.Label
            isRequired
            info="Ratio of loose (dump) volume to in-situ (bank) volume."
            infoPlacement={Placement.Top}
            tooltipClassname="dashboard-modal__lead-distance-tooltip"
          >
            Bulking Factor
          </Input.Label>

          <Input.Number
            placeholder="Enter Value"
            value={entry.bulkingFactor ?? ''}
            isInvalid={!!errors.bulkingFactor}
            error={errors.bulkingFactor}
            onKeyDown={preventNonNumber}
            onChange={handleNumberChange('bulkingFactor')}
            onBlur={() => onFieldBlur(entry.clientId, 'bulkingFactor')}
            onFocus={() => onFieldFocus(entry.clientId, 'bulkingFactor')}
          />
        </InputGroup>
      </div>

      {/* In-situ Volume */}
      <div className="dashboard-modal__lead-distance__summary">
        <span className="dashboard-modal__lead-distance__summary-label">
          IN-SITU VOLUME:
        </span>

        <span>{isNil(inSituVolume) ? '-' : `${inSituVolume} m³`}</span>
      </div>

      {/* Lead Metrics Row */}
      <div className="dashboard-modal__lead-distance__selection-block">
        <InputGroup>
          <Input.Label
            isRequired
            info="Defines the distance travelled between the pit and dump locations."
            infoPlacement={Placement.Top}
            tooltipClassname="dashboard-modal__lead-distance-tooltip"
          >
            Pit-Dump Lead (km)
          </Input.Label>

          <Input.Number
            placeholder="Enter Value"
            value={entry.pitDumpLeadDistanceKm ?? ''}
            isInvalid={!!errors.pitDumpLeadDistanceKm}
            error={errors.pitDumpLeadDistanceKm}
            onKeyDown={preventNonNumber}
            onChange={handleNumberChange('pitDumpLeadDistanceKm')}
            onBlur={() => onFieldBlur(entry.clientId, 'pitDumpLeadDistanceKm')}
            onFocus={() =>
              onFieldFocus(entry.clientId, 'pitDumpLeadDistanceKm')
            }
          />
        </InputGroup>

        <InputGroup>
          <Input.Label
            isRequired
            info="Defines the distance travelled within the pit area during material movement."
            infoPlacement={Placement.Top}
            tooltipClassname="dashboard-modal__lead-distance-tooltip"
          >
            Intra-Pit Lead (km)
          </Input.Label>

          <Input.Number
            placeholder="Enter Value"
            value={entry.intraPitLeadDistanceKm ?? ''}
            isInvalid={!!errors.intraPitLeadDistanceKm}
            error={errors.intraPitLeadDistanceKm}
            onKeyDown={preventNonNumber}
            onChange={handleNumberChange('intraPitLeadDistanceKm')}
            onBlur={() => onFieldBlur(entry.clientId, 'intraPitLeadDistanceKm')}
            onFocus={() =>
              onFieldFocus(entry.clientId, 'intraPitLeadDistanceKm')
            }
          />
        </InputGroup>

        <InputGroup>
          <Input.Label
            isRequired
            info="Defines the distance travelled within the dump area during material movement."
            infoPlacement={Placement.Top}
            tooltipClassname="dashboard-modal__lead-distance-tooltip"
          >
            Intra-Dump Lead (km)
          </Input.Label>

          <Input.Number
            placeholder="Enter Value"
            value={entry.intraDumpLeadDistanceKm ?? ''}
            isInvalid={!!errors.intraDumpLeadDistanceKm}
            error={errors.intraDumpLeadDistanceKm}
            onKeyDown={preventNonNumber}
            onChange={handleNumberChange('intraDumpLeadDistanceKm')}
            onBlur={() =>
              onFieldBlur(entry.clientId, 'intraDumpLeadDistanceKm')
            }
            onFocus={() =>
              onFieldFocus(entry.clientId, 'intraDumpLeadDistanceKm')
            }
          />
        </InputGroup>
      </div>

      {/* Total Lead Distance */}
      <div className="dashboard-modal__lead-distance__summary">
        <span className="dashboard-modal__lead-distance__summary-label">
          TOTAL LEAD DISTANCE:
        </span>

        <span>{clampDecimalInput(String(totalLeadDistance), 2)} km</span>
      </div>
    </div>
  );
};
